/**
 * Ocean Booking Equipment Fields Component
 *
 * Renders the requested equipment section and, if present, the confirmed
 * equipment section (read-only) of the Ocean Booking form.
 *
 * Each requested equipment row contains:
 * - ISOEquipmentCode, units, isShipperOwned
 * - Nested commodities array (collapsible) with description, HSCodes,
 *   weight, weightUnit, volume, volumeUnit, packageCount, packageType
 */

import { useFieldArray, useWatch } from 'react-hook-form';
import { useState } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Common ISO equipment codes for container types
 */
const EQUIPMENT_CODES = [
  { value: '22G1', label: '22G1 - 20ft Dry' },
  { value: '42G1', label: '42G1 - 40ft Dry' },
  { value: '45G1', label: '45G1 - 40ft High Cube Dry' },
  { value: '22R1', label: '22R1 - 20ft Reefer' },
  { value: '42R1', label: '42R1 - 40ft Reefer' },
  { value: '45R1', label: '45R1 - 40ft High Cube Reefer' },
  { value: '22U1', label: '22U1 - 20ft Open Top' },
  { value: '42U1', label: '42U1 - 40ft Open Top' },
  { value: '22P1', label: '22P1 - 20ft Flat Rack' },
  { value: '42P1', label: '42P1 - 40ft Flat Rack' },
  { value: '22T1', label: '22T1 - 20ft Tank' },
  { value: '42T1', label: '42T1 - 40ft Tank' }
];

/**
 * Weight unit options
 */
const WEIGHT_UNITS = [
  { value: 'KGS', label: 'KGS - Kilograms' },
  { value: 'LBS', label: 'LBS - Pounds' }
];

/**
 * Volume unit options
 */
const VOLUME_UNITS = [
  { value: 'CBM', label: 'CBM - Cubic Meters' },
  { value: 'CFT', label: 'CFT - Cubic Feet' }
];

/**
 * Collapsible commodities sub-section within each equipment row
 */
function CommoditiesSection({ equipmentIndex, control, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(true);

  const { fields: commodityFields, append: appendCommodity, remove: removeCommodity } = useFieldArray({
    control,
    name: `requestedEquipment.${equipmentIndex}.commodities`
  });

  const addCommodity = () => {
    appendCommodity({
      description: '',
      HSCodes: '',
      weight: null,
      weightUnit: 'KGS',
      volume: null,
      volumeUnit: 'CBM',
      packageCount: null,
      packageType: ''
    });
  };

  return (
    <div className="border-t pt-3 mt-3">
      {/* Collapsible header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Commodities ({commodityFields.length})
        </button>
        {isEditMode && isOpen && (
          <Button variant="secondary" size="sm" onClick={addCommodity}>
            + Add Commodity
          </Button>
        )}
      </div>

      {/* Commodity entries */}
      {isOpen && (
        <div className="space-y-3 ml-4">
          {commodityFields.length === 0 && (
            <div className="text-center py-2 text-gray-400 text-sm bg-gray-50 rounded">
              No commodities specified.
            </div>
          )}

          {commodityFields.map((commodity, cIdx) => (
            <div key={commodity.id} className="border border-gray-100 rounded p-3 bg-gray-50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Commodity #{cIdx + 1}</span>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => removeCommodity(cIdx)}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {/* Description - full width */}
                <div className="md:col-span-2 lg:col-span-3">
                  <Input
                    label="Description"
                    {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.description`)}
                    disabled={!isEditMode}
                    issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].description`)}
                    placeholder="Commodity description"
                  />
                </div>

                {/* HS Codes (comma-separated) */}
                <Input
                  label="HS Codes"
                  {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.HSCodes`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].HSCodes`)}
                  placeholder="e.g., 8471.30, 8471.41"
                />

                {/* Weight */}
                <Input
                  label="Weight"
                  type="number"
                  {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.weight`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].weight`)}
                />

                {/* Weight Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight Unit</label>
                  <select
                    {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.weightUnit`)}
                    disabled={!isEditMode}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                  >
                    {WEIGHT_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>

                {/* Volume */}
                <Input
                  label="Volume"
                  type="number"
                  {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.volume`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].volume`)}
                />

                {/* Volume Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Volume Unit</label>
                  <select
                    {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.volumeUnit`)}
                    disabled={!isEditMode}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                  >
                    {VOLUME_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>

                {/* Package Count */}
                <Input
                  label="Package Count"
                  type="number"
                  {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.packageCount`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].packageCount`)}
                />

                {/* Package Type */}
                <Input
                  label="Package Type"
                  {...register(`requestedEquipment.${equipmentIndex}.commodities.${cIdx}.packageType`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `requestedEquipment[${equipmentIndex}].commodities[${cIdx}].packageType`)}
                  placeholder="e.g., Carton, Pallet"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * OBEquipmentFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 * @param {Array} [props.confirmedEquipment] - Carrier-confirmed equipment (read-only)
 */
function OBEquipmentFields({ control, register, isEditMode, issues = [], confirmedEquipment = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'requestedEquipment'
  });

  /**
   * Add a new empty equipment entry
   */
  const addEquipment = () => {
    append({
      ISOEquipmentCode: '22G1',
      units: 1,
      isShipperOwned: false,
      commodities: []
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Requested Equipment
        </h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addEquipment}>
            + Add Equipment
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No equipment requested yet. Click "Add Equipment" to specify containers.
        </div>
      )}

      {/* Equipment entries */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="border border-gray-200 rounded-lg p-4 space-y-3"
        >
          {/* Equipment header with remove button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Equipment #{index + 1}
            </span>
            {isEditMode && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>

          {/* Equipment fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* ISO Equipment Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ISO Equipment Code
              </label>
              <select
                {...register(`requestedEquipment.${index}.ISOEquipmentCode`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {EQUIPMENT_CODES.map(code => (
                  <option key={code.value} value={code.value}>{code.label}</option>
                ))}
              </select>
            </div>

            {/* Units */}
            <Input
              label="Units"
              type="number"
              {...register(`requestedEquipment.${index}.units`, { valueAsNumber: true })}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `requestedEquipment[${index}].units`)}
            />

            {/* Shipper Owned checkbox */}
            <div className="flex items-end pb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register(`requestedEquipment.${index}.isShipperOwned`)}
                  disabled={!isEditMode}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Shipper Owned Container</span>
              </label>
            </div>
          </div>

          {/* Nested commodities section (collapsible) */}
          <CommoditiesSection
            equipmentIndex={index}
            control={control}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      ))}

      {/* Confirmed Equipment section (read-only, shown when carrier has responded) */}
      {confirmedEquipment.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">
            Confirmed Equipment
          </h3>

          {confirmedEquipment.map((equip, index) => (
            <div
              key={index}
              className="border border-green-200 bg-green-50 rounded-lg p-4 mb-3"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">
                  Confirmed #{index + 1}
                </span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Confirmed
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">ISO Equipment Code</label>
                  <p className="text-sm text-gray-900">{equip.ISOEquipmentCode || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Units</label>
                  <p className="text-sm text-gray-900">{equip.units ?? '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Shipper Owned</label>
                  <p className="text-sm text-gray-900">{equip.isShipperOwned ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {/* Confirmed commodities */}
              {equip.commodities && equip.commodities.length > 0 && (
                <div className="mt-3 border-t border-green-200 pt-3">
                  <h5 className="text-xs font-medium text-gray-500 mb-2">Commodities</h5>
                  {equip.commodities.map((comm, cIdx) => (
                    <div key={cIdx} className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">{comm.description || 'N/A'}</span>
                      {comm.weight && <span className="ml-2 text-gray-500">| {comm.weight} {comm.weightUnit}</span>}
                      {comm.packageCount && <span className="ml-2 text-gray-500">| {comm.packageCount} {comm.packageType}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OBEquipmentFields;
